// src/api/agentService.js
import apiClient from './axiosConfig';

const API_URL = process.env.REACT_APP_API_URL || '';

const handleApiError = (error, operation) => {
  console.error(`${error.config?.method?.toUpperCase()} ${error.config?.url} - Error ${operation}:`, error);
  let message = 'An unknown error occurred.';
  const status = error.response?.status;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
        message = d.detail;
    } else if (Array.isArray(d.detail)) {
        message = d.detail.map(err => {
            const loc = err.loc?.slice(1).join('.') || 'field';
            return `${loc}: ${err.msg}`;
        }).join('; ');
         if (!message) message = "Validation error";
    } else if (typeof d === 'string' && d.length < 500) {
        message = d;
    } else if (d.message && typeof d.message === 'string') {
        message = d.message;
    } else if (status) {
         message = `Request failed with status code ${status}`;
    }
     if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Request failed with status code ${status || 'unknown'}`;
     }
  } else if (error.message) {
    message = error.message;
  }

  return {
    isError: true,
    message,
    status: status,
    originalError: error
  };
};

export const agentService = {
  /**
   * GET /api/v1/agents
   * Auth: User JWT
   * Response: List of AgentResponse models.
   */
  listAgents: async () => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/agents`);
      return data; // Array<AgentResponse>
    } catch (err) {
      return handleApiError(err, 'listing agents');
    }
  },

  /**
   * GET /api/v1/agents/{agent_id}
   * Auth: User JWT
   * Response: AgentResponse model.
   */
  getAgent: async (agentId) => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/agents/${agentId}`);
      return data; // AgentResponse
    } catch (err) {
      return handleApiError(err, `fetching agent ${agentId}`);
    }
  },

  /**
   * POST /api/v1/agents
   * Auth: User JWT
   * Request Body: AgentCreate model (name, llm_model, description?, agent_settings?, is_public?, tool_ids? [slugs])
   * Response: AgentResponse model for the created agent.
   * Note: system_prompt_id is REMOVED from AgentCreate.
   */
  createAgent: async (agentData) => {
    try {
      // Ensure system_prompt_id is not sent, as it's removed in V2
      const { system_prompt_id, ...payload } = agentData;
      const { data } = await apiClient.post(`${API_URL}/api/v1/agents`, payload);
      return data; // AgentResponse
    } catch (err) {
      return handleApiError(err, 'creating agent');
    }
  },

  /**
   * PUT /api/v1/agents/{agent_id}
   * Auth: User JWT
   * Request Body: AgentUpdate model (name?, description?, llm_model?, is_public?, agent_settings?, tool_ids? [slugs])
   * Response: AgentResponse model of the updated agent.
   * Note: system_prompt_id is REMOVED from AgentUpdate.
   */
  updateAgent: async (agentId, updateData) => {
    try {
      // Ensure system_prompt_id is not sent
      const { system_prompt_id, ...payload } = updateData;
      const { data } = await apiClient.put(
        `${API_URL}/api/v1/agents/${agentId}`,
        payload
      );
      return data; // AgentResponse
    } catch (err) {
      return handleApiError(err, `updating agent ${agentId}`);
    }
  },

  /**
   * DELETE /api/v1/agents/{agent_id}
   * Auth: User JWT
   * Response: 204 No Content on success.
   */
  deleteAgent: async (agentId) => {
    try {
      await apiClient.delete(`${API_URL}/api/v1/agents/${agentId}`);
      return { success: true }; // 204 No Content on success
    } catch (err) {
      return handleApiError(err, `deleting agent ${agentId}`);
    }
  },

  /**
   * GET /api/v1/agents/{agent_id}/tools
   * Auth: User JWT
   * Response: List[str] (list of tool slugs).
   */
  getAgentTools: async (agentId) => {
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/agents/${agentId}/tools`
      );
      return data; // Array<string> (tool slugs)
    } catch (err) {
      return handleApiError(err, `fetching tools for agent ${agentId}`);
    }
  },

  /**
   * PUT /api/v1/agents/{agent_id}/tools
   * Auth: User JWT
   * Request Body: List[str] (list of tool slugs).
   * Response: List[str] (the new list of tool slugs linked to the agent).
   */
  updateAgentTools: async (agentId, toolSlugs) => {
    try {
      const { data } = await apiClient.put(
        `${API_URL}/api/v1/agents/${agentId}/tools`,
        toolSlugs // Send the array of slugs directly as the body
      );
      return data; // Array<string> (updated list of tool slugs)
    } catch (err) {
      return handleApiError(err, `updating tools for agent ${agentId}`);
    }
  },

  // --- V2 Agent-Prompt Specific Methods (Section 4.2 of V2 Doc) ---

  /**
   * GET /api/v1/agents/{agent_id}/prompts
   * Auth: User JWT
   * Response: List[PromptResponse]
   */
  listPromptsForAgent: async (agentId) => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/agents/${agentId}/prompts`);
      return data; // Array<PromptResponse>
    } catch (err) {
      return handleApiError(err, `listing prompts for agent ${agentId}`);
    }
  },

  /**
   * GET /api/v1/agents/{agent_id}/prompts/active
   * Auth: User JWT
   * Response: Optional[PromptResponse] (null if no prompt is active for the agent).
   */
  getActivePromptForAgent: async (agentId) => {
    try {
      const { data } = await apiClient.get(`${API_URL}/api/v1/agents/${agentId}/prompts/active`);
      // FastAPI returns 200 with null body for Optional[Model] if None
      return data;
    } catch (err) {
      // Some backends might 404 if no active prompt, though FastAPI usually 200 with null
      if (err.response && err.response.status === 404) {
        return null;
      }
      return handleApiError(err, `fetching active prompt for agent ${agentId}`);
    }
  },

  /**
   * POST /api/v1/agents/{agent_id}/prompts/{prompt_id}/activate
   * Auth: User JWT
   * Response: PromptResponse of the activated prompt.
   */
  setActivePromptForAgent: async (agentId, promptId) => {
    try {
      const { data } = await apiClient.post(`${API_URL}/api/v1/agents/${agentId}/prompts/${promptId}/activate`);
      return data; // PromptResponse
    } catch (err) {
      return handleApiError(err, `activating prompt ${promptId} for agent ${agentId}`);
    }
  },

  /**
   * PUT /api/v1/agents/{agent_id}/prompts/{prompt_id}/link
   * Auth: User JWT
   * Response: 204 No Content.
   */
  linkPromptToAgent: async (agentId, promptId) => {
    try {
      await apiClient.put(`${API_URL}/api/v1/agents/${agentId}/prompts/${promptId}/link`);
      return { success: true };
    } catch (err) {
      return handleApiError(err, `linking prompt ${promptId} to agent ${agentId}`);
    }
  },

  /**
   * DELETE /api/v1/agents/{agent_id}/prompts/{prompt_id}/unlink
   * Auth: User JWT
   * Response: 204 No Content.
   */
  unlinkPromptFromAgent: async (agentId, promptId) => {
    try {
      await apiClient.delete(`${API_URL}/api/v1/agents/${agentId}/prompts/${promptId}/unlink`);
      return { success: true };
    } catch (err) {
      return handleApiError(err, `unlinking prompt ${promptId} from agent ${agentId}`);
    }
  },
};